import React, { useEffect, useRef, useState } from 'react';
import img1b from '../assets/1b.avif';
import img2b from '../assets/2b.avif';
import img3b from '../assets/3b.jpeg';
import thirdImg from '../assets/3rd_image_in_about.avif';
import imga1 from '../assets/a1.avif';
import imga2 from '../assets/a2.avif';
import imga3 from '../assets/a3.avif';
import ashwinImg from '../assets/ashwin.avif';
import nicoleImg from '../assets/nicole.avif';
import './About.css';

const About = () => {  
  const coachesRef = useRef(null);
  const advisorsRef = useRef(null);
  const approachRef = useRef(null);
  const missionRef = useRef(null);
  const [missionTypingText, setMissionTypingText] = useState('');
  const [visionTypingText, setVisionTypingText] = useState('');
  const [approachTypingText, setApproachTypingText] = useState('');
  const [isMissionTyping, setIsMissionTyping] = useState(false);
  const [isVisionTyping, setIsVisionTyping] = useState(false);
  const [isApproachTyping, setIsApproachTyping] = useState(false);
  const approachDescRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const refs = [coachesRef, advisorsRef, approachRef, missionRef, approachDescRef];
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  // Typing animation effect for approach text
  useEffect(() => {
    const approachText = 'At Mobius Services, we help job seekers grow their careers with a simple three-step plan: Application Strategy, Positioning Strategy, and Execution Strategy. This approach is about making your job hunt and career path clearer and more effective.';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            if (!isApproachTyping) {
              setIsApproachTyping(true);
              let index = 0;
              const typeText = () => {
                if (index < approachText.length) {
                  setApproachTypingText(approachText.slice(0, index + 1));
                  index++;
                  setTimeout(typeText, 30);
                }
              };
              typeText();
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    if (approachDescRef.current) {
      observer.observe(approachDescRef.current);
    }

    return () => {
      if (approachDescRef.current) {
        observer.unobserve(approachDescRef.current);
      }
    };
  }, [isApproachTyping]);

  // Typing animation effect for mission text
  useEffect(() => {
    const missionText = 'Mobius Services LLC - Our mission is to remove the angst from the job search process and to help all job seekers to have a successful outcome. Whether you are transitioning to a new role or simply exploring your options, our trained recruiting team, AI assisted methodologies, and application approach maximize your chances of success. We allow job seekers to focus on networking, upskilling, and ultimately helping them land a job in record time.';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isMissionTyping) {
            setIsMissionTyping(true);
            let index = 0;
            const typeText = () => {
              if (index < missionText.length) {
                setMissionTypingText(missionText.slice(0, index + 1));
                index++;
                setTimeout(typeText, 30);
              }
            };
            typeText();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (missionRef.current) {
      observer.observe(missionRef.current);
    }

    return () => {
      if (missionRef.current) {
        observer.unobserve(missionRef.current);
      }
    };
  }, [isMissionTyping]);

  // Typing animation effect for vision text
  useEffect(() => {
    const visionText = 'Finding a new job should not be so difficult, and every individual should be able to maximize their value from new employment. Our vision is to make the job search a one-click, fully managed process for job seekers. We aim to provide a seamless and efficient experience for individuals seeking their next career opportunity.';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisionTyping) {
            setIsVisionTyping(true);
            let index = 0;
            const typeText = () => {
              if (index < visionText.length) {
                setVisionTypingText(visionText.slice(0, index + 1));
                index++;
                setTimeout(typeText, 30);
              }
            };
            typeText();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (missionRef.current) {
      observer.observe(missionRef.current);
    }

    return () => {
      if (missionRef.current) {
        observer.unobserve(missionRef.current);
      }
    };
  }, [isVisionTyping]);

  return (
    <div className="about-page">
      {/* About Our Founders */}
      <div className="about-page-container">
        <h1 className="about-page-title">About Our Founders</h1>

        <div className="founder-profile">
          <div className="founder-text">
            <h2 className="founder-name">I'm Ashwin.</h2>

            <p className="founder-description">
              Ashwin is an accomplished senior executive with over 20
              years of experience in cloud infrastructure and financial
              services. With over 2 decades of experience at Google and
              JP Morgan, Ashwin held various sales and GTM roles.
              Ashwin is an MBA holder from Yale University.
            </p>

            <p className="founder-description">
              While going through his job search journey and helping others
              do the same, he productized this approach. This was the
              beginning of Mobius. Ashwin is passionate about benefiting
              others from his experiences and adding value to their job
              search journey.
            </p>

            <p className="founder-description">
              Ashwin's vision with Mobius is to give job seekers a significant
              advantage in securing the roles of their dreams.
            </p>

            <p className="founder-linkedin">
              Ashwin's <a href="#" className="linkedin-link">linkedin</a>
            </p>
          </div>

          <div className="founder-image">
            <img src={ashwinImg} alt="Ashwin" className="ashwin-img" />
          </div>
        </div>
      </div>

      {/* Our Executive Coaches */}
      <div className="coaches-section" ref={coachesRef}>
        <div className="about-container">
          <h2 className="section-title">Our Executive Coaches</h2>

          <div className="coach-card">
            <img src={nicoleImg} alt="Nicole Lou" className="coach-img" />
            <div className="coach-info">
              <p>
                <strong>Nicole Lou</strong> In a competitive job market, your resume isn't just a
                document—it's a powerful tool that sets you apart. I help
                professionals craft high-impact, ATS-friendly resumes that pass
                screenings and position them as top candidates worth
                interviewing.
              </p>
              <p>
                With a B.S. in Business Administration from UC Berkeley and 7+
                years of experience in AI-driven product strategy, I've seen
                firsthand how the proper positioning opens doors. I can turn
                complex achievements into clear, compelling narratives that grab
                attention. My targeted, results-driven approach helps clients
                confidently stand out and land roles that truly match their skills and
                potential.
              </p>
              <p>
                Nicole is available for resume rebuilds, career coaching and
                interview preparation services.
              </p>
            </div>
          </div>

          <div className="coach-card">
            <img src={thirdImg} alt="Rob Rosen" className="coach-img" />
            <div className="coach-info">
              <p>
                <strong>Rob Rosen</strong> has led customer-facing teams at technology leaders including
                Amazon, Google, NetApp, Check Point Software, and
                Sun Microsystems. He's interviewed thousands of candidates
                across multiple disciplines (CxO staff, engineering, product
                management, product marketing, support, business development),
                directly hired several hundred employees, and promoted more
                than fifty people just during his time at Google and Amazon, where
                he was a Bar Raiser responsible for referencing more than a hundred
                complex hiring decisions across AWS, Amazon Music, and Amazon
                GoodReads.
              </p>
              <p>
                Rob has formally and informally coached many people throughout
                his career, helping them on a variety of tactical ("how should I
                build my promo document?", "what should be my next role?") and
                strategic ("how do I become happier at work?", "where do my
                skills create the most impact?") topics. Committed to creating
                impact at an individualized level, Rob is proud of the fact that many
                of his former employees, peers, and managers have referred to him
                as "one of the best coaches I've ever had."
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Board of Advisors */}
      <section className="advisors-section" ref={advisorsRef}>
        <div className="about-container">
          <h2 className="section-title">Our Board of Advisors</h2>

          <div className="advisor-card">
            <img src={img1b} alt="Vimi Rao" className="advisor-img" />
            <div className="advisor-info">
              <p>
                <strong>Vimi Rao</strong> is a visionary leader and entrepreneur, currently serving as the Founder and CEO of
                Sapta Inc. Her career is marked by influential roles at leading organizations, including Partner
                at CrossRealms Inc., Principal Partner at VitSustain Group Inc, and VP Program Management
                at CrossRealms Inc. Vimi's diverse experience also encompasses significant positions at
                United Airlines, where she managed the worldwide Mileage Plus® membership I-lite
                Qualification program, and strategic roles at renowned institutions like Inventa Technologies,
                ABN AMRO, Price Waterhouse Coopers, Sybase, and Computer Sciences Corporation.
              </p>
            </div>
          </div>

          <div className="advisor-card">
            <img src={img2b} alt="Amit Shanker" className="advisor-img" />
            <div className="advisor-info">
              <p>
                <strong>Amit Shanker</strong> is the Founder and CEO of Bloom AI, Inc., a company dedicated to
                transforming data into actionable business insights through innovative platforms like
                #SynthBI and #DYSTL. His career includes a role as an Advisory Board Member at SG
                Analytics. Amit's extensive experience at Evalueserve, where he served as Global Head of
                Data Analytics and Products for Financial Services, highlights his ability to lead high-growth
                global initiatives. His work at Evalueserve included the development of AI and data analytics
                products and the establishment of key partnerships with industry leaders like AlphaSense
                and Automation Anywhere. Additionally, Amit's has also been involved in the founding the
                Triangle Analytics Leaders (TAL) in North Carolina.
              </p>
            </div>
          </div>

          <div className="advisor-card">
            <img src={img3b} alt="Alex Langowski" className="advisor-img" />
            <div className="advisor-info">
              <p>
                <strong>Alex Langowski</strong> is a distinguished data science and AI professional with extensive experience
                enhancing business operations through data-driven strategies. His tenure as Chief Data
                Officer at Datasential Inc. and VP of Data Science at Thimble and Slice in New York
                showcases his expertise in optimizing data efficiency and implementing advanced analytics
                platforms. Alex's academic credentials include a Bachelor of Science in Business
                Administration and continued education in data science. His proficiency in multiple
                languages and advanced technical skills make him an invaluable advisor in the tech startup
                space, especially for initiatives involving generative AI and machine learning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="approach-section" ref={approachRef}>
        <div className="about-container">
          <h2 className="section-title">Our Approach</h2>

          <div className="approach-images">
            <div className="approach-img-grid">
              <img src={imga1} alt="Application Strategy" className="approach-img" />
              <img src={imga2} alt="Team Meeting" className="approach-img" />
              <img src={imga3} alt="Positioning Strategy" className="approach-img" />
            </div>
          </div>

          <div className="approach-description" ref={approachDescRef}>
            <p className="typing-text">
              {isApproachTyping ? approachTypingText : 'At Mobius Services, we help job seekers grow their careers with a simple three-step plan: Application Strategy, Positioning Strategy, and Execution Strategy. This approach is about making your job hunt and career path clearer and more effective.'}
            </p>
          </div>

          <div className="strategy-details">
            <div className="strategy-item">
              <h4>1. Application Strategy</h4>
              <p>
                First, we figure out the best job market strategy for you. This means understanding jobs, knowing what you're good at, and
                finding where those two things meet. We help you focus on the jobs and fields where your skills and career goals line up well,
                increasing your chances of finding the right job.
              </p>
            </div>

            <div className="strategy-item">
              <h4>2. Positioning Strategy</h4>
              <p>
                Next, we help you look your best on paper and online. This involves ensuring your resume, LinkedIn, and other profiles tell a
                strong, clear story about your career and why you're a great fit for the jobs you want. We work with you to make a narrative that
                catches the attention of recruiters and hiring managers.
              </p>
            </div>

            <div className="strategy-item">
              <h4>3. Execution Strategy</h4>
              <p>
                Lastly, we take action. We apply for many jobs for you, using our technology to find a good fit. We also help you network by
                connecting you with hiring managers and leaders in your field. Our goal is to make things easier for you so you can focus on
                getting ready for interviews and boosting your skills, while we handle the grunt work of the job search.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="mission-section" ref={missionRef}>
        <div className="about-container">
          <div className="mission-content">
            <h2 className="section-title">Our Mission</h2>
            <p className="mission-text typing-text">
              {isMissionTyping ? missionTypingText : 'Mobius Services LLC - Our mission is to remove the angst from the job search process and to help all job seekers to have a successful outcome. Whether you are transitioning to a new role or simply exploring your options, our trained recruiting team, AI assisted methodologies, and application approach maximize your chances of success. We allow job seekers to focus on networking, upskilling, and ultimately helping them land a job in record time.'}
            </p>
          </div>

          <div className="vision-content">
            <h2 className="section-title">Our Vision</h2>
            <p className="vision-text typing-text">
              {isVisionTyping ? visionTypingText : 'Finding a new job should not be so difficult, and every individual should be able to maximize their value from new employment. Our vision is to make the job search a one-click, fully managed process for job seekers. We aim to provide a seamless and efficient experience for individuals seeking their next career opportunity.'}
            </p>
          </div>

          <div className="values-content">
            <h2 className="section-title">Our Values</h2>
            <div className="values-grid">
              <div className="value-item">
                <h4>Innovation</h4>
                <p>
                  We value innovation as a means
                  of continuously improving our
                  services.
                </p>
              </div>
              <div className="value-item">
                <h4>Efficiency</h4>
                <p>
                  We value efficiency as a way of
                  respecting our clients' time and
                  effort.
                </p>
              </div>
              <div className="value-item">
                <h4>Shared Success</h4>
                <p>
                  We value shared success as a key
                  measure of our own success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
};

export default About;
